/* eslint-disable no-case-declarations */
/* eslint-disable complexity */
import { keyBy } from 'lodash';
import { normalizeQueryParams } from 'helpers/search';

export const defaultState = {
  isMapView: false,
  searchWithMap: false,
  hoveredRestaurantId: null,
  restaurantSearchAPI: {
    // We store IDs and key restos by ID to facilitate filering out duplicate restos that might
    // come in during inifite scroll. This is holdover logic from Redux.
    // This could probably be simplified down the line with a filter loop.
    restaurants: [],
    aggregations: {},
    byId: {},
    loadedIds: [],
    areRestaurantsLoading: true,
  },
  newRestaurantsAPI: {
    restaurants: [],
  },
  moreFacetRestaurantsAPI: {
    action: null,
    name: '',
    restaurants: [],
    tag: '',
  },
  restaurantListState: 'default',
  autoCompleteAPI: {
    isLoading: false,
    results: null,
  },
  geocodedSearchedAddress: null, // User input addresses need to be geocoded to get lat/lng values
  selectedRestaurantId: null,
  // All user actions ultimately update the URL - the source of truth for "filters" and params sent to the API
  // The component simply watches the URL for changes and dispatches API calls as a result
  searchFiltersFromURL: {
    deliversToMe: false,
    isOpen: false,
    hasCurbside: false,
    hasPromo: false,
    query: '',
    loc: null,
    lat: null,
    lng: null,
  },
  meta: {
    // This is used for reporting and for making API calls to handle infinite scroll
    next: null,
    count: null,
  },
  filterPanelActive: false,
  autocompletePanelActive: false,
  facetsAPI: {
    facets: [],
  },
};

// Actions
export const SET_FETCHED_RESTAURANTS = 'SET_FETCHED_RESTAURANTS';
export const SET_NEW_RESTAURANTS = 'SET_NEW_RESTAURANTS';
export const SET_GEOCODED_SEARCHED_ADDRESS = 'SET_GEOCODED_SEARCHED_ADDRESS';
export const SET_SEARCH_FILTERS_FROM_URL = 'SET_SEARCH_FILTERS_FROM_URL';
export const SET_SEARCH_METADATA = 'SET_SEARCH_METADATA';
export const SET_FETCHED_AUTOCOMPLETE = 'SET_FETCHED_AUTOCOMPLETE';
export const SET_AUTOCOMPLETE_URL_QUERY = 'SET_AUTCOMPLETE_URL_QUERY';
export const SET_SEARCH_WITH_MAP = 'SET_SEARCH_WITH_MAP';
export const SET_FETCHED_ADDITIONAL_RESTAURANTS = 'SET_FETCHED_ADDITIONAL_RESTAURANTS';
export const SET_HOVERED_RESTAURANT_ID = 'SET_HOVERED_RESTAURANT_ID';
export const SET_IS_MAP_VIEW = 'SET_IS_MAP_VIEW';
export const SET_ARE_RESTAURANTS_LOADING = 'SET_ARE_RESTAURANTS_LOADING';
export const SET_FILTER_PANEL_ACTIVE = 'SET_FILTER_PANEL_ACTIVE';
export const SET_AUTOCOMPLETE_PANEL_ACTIVE = 'SET_AUTOCOMPLETE_PANEL_ACTIVE';
export const SET_RESTAURANT_LIST_STATE = 'SET_RESTAURANT_LIST_STATE';
export const SET_FETCHED_FACETS = 'SET_FETCHED_FACETS';
export const SET_MORE_FACET_RESTAURANTS = 'SET_MORE_FACET_RESTAURANTS';

// Helpers
export function matchLoadedIdstoRestaurants(loadedIds, byId) {
  return loadedIds.map((restaurantId) => byId[restaurantId]);
}

export function shapeRestaurantSearchAPIResponse(state, payload) {
  if (!payload) return { ...state };
  const { restaurants = [], total, next } = payload.searchResults;
  const byId = keyBy(restaurants, 'id');
  const loadedIds = restaurants.map((restaurant) => restaurant.id);
  return {
    ...state,
    restaurantSearchAPI: {
      restaurants: matchLoadedIdstoRestaurants(loadedIds, byId),
      aggregations: payload.searchResults.aggregations,
      byId,
      loadedIds,
      areRestaurantsLoading: false,
    },
    meta: {
      total,
      next,
    },
  };
}

// TODO this logic came over from Redux. We should aim to simplify. Ticket below:
// https://chownow.atlassian.net/browse/CN-22036
export function shapeAdditionalRestaurantsAPIResponse(state, payload) {
  if (!payload) return { ...state };
  const { limit, offset, next, prev, restaurants = [], total } = payload;

  const previousRestaurantSearchAPI = state.restaurantSearchAPI;

  const updatedById = {
    ...previousRestaurantSearchAPI.byId,
    ...keyBy(restaurants, 'id'),
  };

  // Dedupe IDs
  const updatedLoadedIds = [
    ...new Set([
      ...previousRestaurantSearchAPI.loadedIds,
      ...restaurants.map((restaurant) => restaurant.id),
    ]),
  ];

  const updatedRestaurantSearchAPI = {
    ...previousRestaurantSearchAPI,
    byId: updatedById,
    loadedIds: updatedLoadedIds,
    restaurants: matchLoadedIdstoRestaurants(updatedLoadedIds, updatedById),
  };
  return {
    ...state,
    restaurantSearchAPI: updatedRestaurantSearchAPI,
    meta: {
      ...state.meta,
      limit,
      offset,
      next,
      prev,
      total,
    },
  };
}

export function shapeFacetsAPIResponse(state, payload) {
  return {
    ...state,
    facetsAPI: {
      facets: payload?.facets || [],
    },
  };
}

export function reducer(state, action) {
  const { payload = {} } = action;
  switch (action.type) {
    case SET_GEOCODED_SEARCHED_ADDRESS:
      return { ...state, geocodedSearchedAddress: payload };
    case SET_FETCHED_RESTAURANTS:
      return shapeRestaurantSearchAPIResponse(state, action.payload);
    case SET_NEW_RESTAURANTS:
      return {
        ...state,
        newRestaurantsAPI: {
          restaurants: payload,
        },
      };
    case SET_MORE_FACET_RESTAURANTS:
      return {
        ...state,
        moreFacetRestaurantsAPI: payload,
      };
    case SET_ARE_RESTAURANTS_LOADING:
      return {
        ...state,
        restaurantSearchAPI: {
          ...state.restaurantSearchAPI,
          areRestaurantsLoading: payload,
        },
      };
    case SET_SEARCH_FILTERS_FROM_URL:
      // The API requires "1" and "0" strings for filter states. Cast these back to booleans for the UI.
      const normalizedQueryParams = normalizeQueryParams(payload);
      return {
        ...state,
        searchFiltersFromURL: {
          ...state.searchFiltersFromURL,
          ...normalizedQueryParams,
        },
      };
    case SET_FETCHED_AUTOCOMPLETE:
      return {
        ...state,
        autoCompleteAPI: {
          isLoading: payload.isLoading,
          results: payload.results,
        },
      };
    case SET_SEARCH_WITH_MAP:
      return {
        ...state,
        searchWithMap: !!payload,
      };
    case SET_FETCHED_ADDITIONAL_RESTAURANTS:
      return shapeAdditionalRestaurantsAPIResponse(state, action.payload);
    case SET_HOVERED_RESTAURANT_ID:
      return {
        ...state,
        hoveredRestaurantId: payload,
      };
    case SET_IS_MAP_VIEW:
      return {
        ...state,
        isMapView: !!payload,
      };
    case SET_FILTER_PANEL_ACTIVE:
      return {
        ...state,
        filterPanelActive: !!payload,
      };
    case SET_AUTOCOMPLETE_PANEL_ACTIVE:
      return {
        ...state,
        autocompletePanelActive: !!payload,
      };
    case SET_RESTAURANT_LIST_STATE:
      return {
        ...state,
        restaurantListState: payload,
      };
    case SET_FETCHED_FACETS:
      return shapeFacetsAPIResponse(state, action.payload);
    default:
      return {};
  }
}
